import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Target, Linkedin } from "lucide-react";
import { MonogramAvatar } from "../components/MonogramAvatar";

const founders = [
  {
    name: "Puneeth Nagendra",
    role: "Co-Founder",
    bio: "Entrepreneur and EdTech innovator with a strong focus on building scalable digital ecosystems and learner-centric platforms. Recognized for research and innovation in education technology.",
  },
  {
    name: "Sowmya Sairam",
    role: "Co-Founder",
    bio: "Education-focused professional with expertise in academic development and operational leadership, contributing to building impactful learning solutions.",
  },
  {
    name: "Nirosha H V",
    role: "Co-Founder",
    bio: "Dedicated academic and program specialist with experience in student development and educational initiatives, focused on delivering meaningful learning outcomes.",
  },
];

export const AboutPage = () => {
  return (
    <main data-testid="about-page">
      {/* HEADER */}
      <section
        data-testid="about-header-section"
        className="bg-white border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 mb-5">
              About SparkNext Pathways
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.05]">
              Building the foundation for financially responsible generations.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed">
              SparkNext Pathways Private Limited is an EdTech + FinTech company
              bridging the gap between education and real-world financial
              skills through technology-driven solutions.
            </p>
          </div>
        </div>
      </section>

      {/* COMPANY OVERVIEW */}
      <section
        data-testid="about-overview-section"
        className="py-20 md:py-28 bg-slate-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 mb-3">
                Company Overview
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                Where education meets finance.
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-5 text-lg leading-relaxed text-slate-600">
              <p>
                Financial literacy is one of the most critical life skills — and
                yet it remains one of the most underserved areas in K-12
                education. SparkNext Pathways exists to change that.
              </p>
              <p>
                We design learning experiences, digital platforms and
                institutional tools that help students understand money
                meaningfully — from saving and spending to earning and planning.
                Our work sits at the intersection of pedagogy, product design
                and fintech, crafted specifically for the rhythms of Indian
                schools and institutions.
              </p>
              <p>
                We partner with schools, CSR foundations and education networks
                to deliver structured, measurable and engaging financial
                literacy at scale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section
        data-testid="vision-mission-section"
        className="py-20 md:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              data-testid="vision-card"
              className="rounded-xl border border-slate-200 p-10 bg-white hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 rounded-lg bg-slate-900 text-white flex items-center justify-center mb-6">
                <Eye className="w-5 h-5" />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 mb-3">
                Our Vision
              </p>
              <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
                To make financial literacy a fundamental skill for every
                student.
              </h3>
            </div>
            <div
              data-testid="mission-card"
              className="rounded-xl border border-slate-200 p-10 bg-slate-900 text-white"
            >
              <div className="w-12 h-12 rounded-lg bg-amber-500 text-slate-900 flex items-center justify-center mb-6">
                <Target className="w-5 h-5" />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400 mb-3">
                Our Mission
              </p>
              <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-white leading-tight">
                To empower students with practical financial knowledge using
                innovative digital ecosystems.
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDERS */}
      <section
        data-testid="founders-section"
        className="py-20 md:py-32 bg-slate-50 border-t border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 mb-4">
              Founders
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              The team building SparkNext Pathways.
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              A multidisciplinary founding team combining entrepreneurship,
              education and academic expertise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {founders.map((f, idx) => (
              <div
                key={f.name}
                data-testid={`founder-card-${idx}`}
                className="bg-white border border-slate-200 rounded-xl p-8 text-center hover:shadow-md transition-shadow"
              >
                <MonogramAvatar
                  name={f.name}
                  testId={`founder-monogram-${idx}`}
                />
                <h3 className="text-lg font-semibold text-slate-900 tracking-tight">
                  {f.name}
                </h3>
                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-amber-500">
                  {f.role}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">
                  {f.bio}
                </p>
                <div className="mt-6 pt-6 border-t border-slate-100 flex items-center justify-center gap-2 text-xs text-slate-400">
                  <Linkedin className="w-3.5 h-3.5" />
                  Profile coming soon
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Want to learn more about what we're building?
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/services"
              data-testid="about-cta-services"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-900 text-white px-6 py-3.5 text-base font-semibold hover:bg-slate-800 transition-colors"
            >
              Explore Services
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-slate-900 border border-slate-200 px-6 py-3.5 text-base font-semibold hover:bg-slate-50 transition-colors"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
